import { Menu as MenuIcon } from "lucide-react";
import React, { useCallback, useLayoutEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { fetchCompanyDetails } from "../../../../../api/action/company.jsx"

import {
  getAllProductCategories,
  getAllProducts,
} from "../../../../../api/action/products.jsx";


import FilterPanel from "./FilterPanel.jsx";
import ImageSlider from "./ProductSlider.jsx";
// import ProductsList from "../ProductTiles.jsx";
import { Loader } from "../../../../../components/Loader.jsx";
import { academyLogger } from "utils/logger";

const Products = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const categoryFromParams = queryParams.get("category") || "All";
  const fullUrl = window.location.href;
  const domain = fullUrl.split("/").slice(0, 3).join("/");
  const companyPrimaryId = useSelector((state) => state.companyReducer.company?.primaryId);

  const [/*newProducts*/, setNewProducts] = useState([]);
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedPriceRange, setSelectedPriceRange] = useState([]);
  const [maxPrice, setMaxPrice] = useState(0);
  const [sortingOrder, setSortingOrder] = useState(null);
  const [hamburgerOpen, setHamburgerOpen] = useState(false);

  const fetchProducts = useCallback(async (company) => {
    try {
      let products = await getAllProducts(company)(dispatch);
      products = Array.isArray(products) ? products : [];
      if (categoryFromParams && categoryFromParams !== "All") {
        products = products.filter(
          (product) => product.category?.name === categoryFromParams
        );
      }
      setNewProducts(products);
      setCategoryProducts(products);
      setIsLoading(false);
      const res = await dispatch(getAllProductCategories(company));
      setCategories([{ code: "All", name: "All" }, ...(Array.isArray(res) ? res : [])]);
      const highestPrice = products.length
        ? Math.max(...products.map((product) => parseFloat(product.price.priceAmt)))
        : 0;
      const roundedMaxPrice = Math.ceil(highestPrice);
      setMaxPrice(roundedMaxPrice);
      setSelectedPriceRange([0, roundedMaxPrice]);
    } catch (error) {
      academyLogger.error("Error fetching products:", error);
      setIsLoading(false);
    }
  }, [categoryFromParams, dispatch]);

  useLayoutEffect(() => {
    if (!companyPrimaryId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    fetchCompanyDetails(domain)
      .then(() => {
        fetchProducts(companyPrimaryId);
      })
      .catch((error) => {
        academyLogger.error("Error fetching company details:", error);
        setIsLoading(false);
      });
  }, [companyPrimaryId, domain, fetchProducts]);

  const handleCategoryChange = (filterVal) => {
    setSelectedCategories((prev) =>
      prev.includes(filterVal.code)
        ? prev.filter((code) => code !== filterVal.code)
        : [...prev, filterVal.code]
    );
  };

  const handleClearFilters = () => {
    setSelectedCategories([]);
    setSelectedPriceRange([0, maxPrice]);
    setSortingOrder(null);
  };

  const toggleSortingOrder = (order) => {
    setSortingOrder(order);
  };

  const filteredProducts = categoryProducts.filter((product) => {
    const priceAmt = parseFloat(product.price.priceAmt);
    const categoryCode = product.category ? product.category.code : "";
    return (
      (selectedCategories.length === 0 ||
        selectedCategories.includes(categoryCode) ||
        selectedCategories.includes("All")) &&
      priceAmt >= selectedPriceRange[0] &&
      priceAmt <= selectedPriceRange[1]
    );
  });

  return (
    <div className="flex flex-col items-center p-4">
      {isLoading ? (
        <div className="flex items-center justify-center min-h-screen">
          <Loader />
        </div>
      ) : (
        <>
          {/* Hamburger Button */}
          <button
            onClick={() => setHamburgerOpen(!hamburgerOpen)}
            className="absolute top-4 left-0 z-50 text-2xl font-bold bg-transparent hover:bg-gray-200 rounded p-2"
            sx={{ color: "#1976d2" }}
          >
            {hamburgerOpen ? "" : <MenuIcon fontSize="inherit" />}{" "}
            {/* inherit font size from the button */}
          </button>

          <div className="flex w-full">
            {/* Adjust width based on hamburger state */}
            <div
              className={`filter-panel-container transition-transform duration-300 ease-in-out ${hamburgerOpen ? "translate-x-0" : "-translate-x-full"
                } fixed top-0 left-0 w-1/4 h-full bg-white shadow-lg z-40`}
            >
              <FilterPanel
                categories={categories}
                selectedCategories={selectedCategories}
                handleCategoryChange={handleCategoryChange}
                sortingOrder={sortingOrder}
                toggleSortingOrder={toggleSortingOrder}
                handleClearFilters={handleClearFilters}
                selectedPriceRange={selectedPriceRange}
                setSelectedPriceRange={setSelectedPriceRange}
                maxPrice={maxPrice}
                setFilterVisible={setHamburgerOpen}
                filteredProducts={filteredProducts}
              />
            </div>

            <div
              className={`flex flex-col w-full transition-all duration-300 ${hamburgerOpen ? "md:w-3/4 ml-1/4" : "md:w-full"
                } overflow-y-auto`}
              style={{ marginLeft: hamburgerOpen ? "25%" : "0" }}
            >

              <ImageSlider />
              {/* uncomment befor using this template */}
              {/* <ProductsList products={filteredProducts} /> */}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Products;
